import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Translate } from 'react-localize-redux';
import { InsurerAppointments, PayerCategory } from '../types';

interface InsuranceTopTableProps {
  insurers: InsurerAppointments[];
  /**
   * Which payer categories this table reports on. A prop rather than two components because the
   * two instances — Top 5 IPM and Top 5 assurances — differ in nothing else.
   */
  payerCategories: PayerCategory[];
  title: string;
  /** From the row's `topN`, the same knob the insurance pie beside it uses for its own cap. */
  limit?: number;
}

/**
 * SHER-899. The largest insurers of one payer category, by appointments.
 *
 * The pie beside it caps at five slices and rolls the tail into "Other", which answers "how is
 * the caseload split" and cannot answer "who are our biggest payers" — the tail is exactly where
 * that question lives. These tables answer the second question, which is why they are tables:
 * the insurer NAME is the point, and a pie has nowhere to put forty of them.
 */
const InsuranceTopTable: React.FC<InsuranceTopTableProps> = ({
  insurers,
  payerCategories,
  title,
  limit = 5,
}) => {
  const rows = useMemo(() => {
    const matching = insurers.filter((insurer) => payerCategories.includes(insurer.payerCategory));
    const total = matching.reduce((sum, insurer) => sum + insurer.appointments, 0);

    return [...matching]
      .sort((a, b) => b.appointments - a.appointments)
      .slice(0, limit)
      // The share is of this CATEGORY, not of all appointments — and of the whole category, not
      // of the rows shown. A table headed "Top 5 IPM" whose percentages were of the whole
      // caseload would read as though IPM covered 4% of the hospital rather than 40% of IPM.
      .map((insurer) => ({
        ...insurer,
        share: total > 0 ? ((insurer.appointments / total) * 100).toFixed(1) : '0.0',
      }));
  }, [insurers, payerCategories, limit]);

  return (
    <Box>
      <Typography variant="h6">{title}</Typography>

      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          <Translate id="hospital.analytics.insurance_top.empty" />
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><Translate id="hospital.analytics.insurance_top.insurer" /></TableCell>
                <TableCell><Translate id="hospital.analytics.insurance_top.code" /></TableCell>
                <TableCell align="right"><Translate id="hospital.analytics.insurance_top.appointments" /></TableCell>
                <TableCell align="right"><Translate id="hospital.analytics.insurance_top.share" /></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((insurer) => (
                <TableRow key={insurer.code}>
                  <TableCell>{insurer.name}</TableCell>
                  <TableCell>{insurer.code}</TableCell>
                  <TableCell align="right">{insurer.appointments}</TableCell>
                  <TableCell align="right">{`${insurer.share}%`}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default InsuranceTopTable;
